import React, { Component } from "react";
import { Frame, FrameRiskTree } from "../type";

type Props = {
  sceneX: number;
  sceneH: number;
  frame: Frame;
  frameRiskTree: FrameRiskTree;
  riskColorMap: Map<string, string>;
  riskEncode?: "angle" | "height" | "mixed";
  R_RATIO: number;
  callbackFn: (focusRiskLabel: string) => void;
};

type State = {
  cx: number;
  cy: number;
  innerR: number;
  outerR: number;
  riskTypes: string[];
  riskLabels: string[];
  riskStore: Map<string, number>;
  riskMax: number;
  focusRiskLabel: string | null;
  frameSizeScale: number;
};

const riskThreshold = 1e-3;

export default class RadialBarGlyph extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { frame, frameRiskTree } = props;
    const riskLabels = [];
    const riskStore = new Map();
    let sum = 0;
    for (const riskType in frameRiskTree) {
      for (const riskLabel in frameRiskTree[riskType]) {
        if (frameRiskTree[riskType][riskLabel] > riskThreshold) {
          sum += frameRiskTree[riskType][riskLabel];
        }
      }
    }
    let riskMax = 0;
    for (const riskType in frameRiskTree) {
      for (const riskLabel in frameRiskTree[riskType]) {
        const _riskScore = frameRiskTree[riskType][riskLabel];
        const _riskLabel = `${riskType}_${riskLabel}`;
        if (_riskScore > riskThreshold) {
          riskLabels.push(_riskLabel);
          riskStore.set(_riskLabel, _riskScore / sum);
          if (_riskScore / sum > riskMax) {
            riskMax = _riskScore / sum;
          }
        }
      }
    }

    // new
    const frameSizeScale = 0.8;

    const innerR = (frame.size[0] / 2) * frameSizeScale;
    const outerR = Math.min(props.sceneH, props.sceneX) / 2;
    this.state = {
      cx: props.sceneX - outerR - 80,
      cy: props.sceneH / 2,
      innerR: innerR,
      outerR: outerR,
      riskTypes: Object.keys(frameRiskTree),
      riskLabels: riskLabels,
      riskStore: riskStore,
      riskMax: riskMax,
      focusRiskLabel: null,
      frameSizeScale: frameSizeScale,
    };
  }

  render() {
    const {
      frame,
      riskColorMap,
      R_RATIO,
      riskEncode,
      sceneX,
      sceneH,
    } = this.props;
    const svgW = sceneX;
    const svgH = sceneH;
    const G_RATIO = R_RATIO * 0.8;
    const {
      cx,
      cy,
      innerR,
      outerR,
      riskLabels,
      riskStore,
      riskMax,
      focusRiskLabel,
      frameSizeScale,
    } = this.state;
    let startAngle = 0;
    const PieSectors = riskLabels.map((riskLabel) => {
      let endAngle,
        _outerR = outerR;
      let proportion = riskStore.get(riskLabel);
      if (proportion === 1) {
        proportion = 0.999;
      }
      if (riskEncode === "angle") {
        endAngle = startAngle + proportion * Math.PI * 2;
        // _outerR = innerR * 2 * G_RATIO;
        _outerR = innerR * (1 + (riskMax / riskMax) * G_RATIO);
      } else if (riskEncode === "height") {
        endAngle =
          startAngle +
          ((riskLabels.length === 1 ? 0.999 : 1) * Math.PI * 2) /
            riskLabels.length;
        _outerR = innerR * (1 + (proportion / riskMax) * G_RATIO);
      } else {
        endAngle = startAngle + proportion * Math.PI * 2;
        _outerR = innerR * (1 + (proportion / riskMax) * G_RATIO);
      }
      const pathD = generatePieSectorPathD(
        cx,
        cy,
        innerR,
        _outerR,
        startAngle,
        endAngle
      );
      const focusPathD = generatePieSectorPathD(
        cx,
        cy,
        innerR,
        innerR * (1 + R_RATIO),
        startAngle,
        endAngle
      );
      startAngle = endAngle;
      const [riskType, riskTag] = riskLabel.split("_");
      const riskColor = riskColorMap.get(riskType);
      return (
        <g
          key={`scene-${frame.scene}-${riskLabel}`}
          onClick={() => this.props.callbackFn(riskLabel)}
          onMouseEnter={() => this.setState({ focusRiskLabel: riskLabel })}
          onMouseLeave={() => this.setState({ focusRiskLabel: null })}
        >
          <path
            id={`scene${frame.scene}${riskLabel}`}
            style={{ fill: riskColor, stroke: "#fff", strokeWidth: 0.5 }}
            d={focusRiskLabel === riskLabel ? focusPathD : pathD}
          />
          {focusRiskLabel === riskLabel && (
            <g>
              <rect
                key={`tooltip-${frame.scene}-${riskType}-${riskTag}`}
                x={120}
                y={30}
                width={70}
                height={60}
                style={{
                  fill: riskColor,
                  fillOpacity: 0.6,
                  stroke: riskColor,
                  strokeWidth: 2,
                }}
                rx={5}
                ry={5}
              ></rect>
              <text x={125} y={45} fill="#fff">
                {riskType}
              </text>
              <text x={125} y={45 + 15} fill="#fff">
                {riskTag}
              </text>
              <text x={125} y={45 + 15 * 2} fill="#fff">
                {proportion.toFixed(4)}
              </text>
            </g>
          )}
        </g>
      );
    });
    // hard code
    // frame.size[0] = 82.782;
    // frame.size[1] = 82.782;
    return (
      <div className="raidal-bar-frame">
        <svg
          width={svgW}
          height={svgH}
          style={{ position: "absolute", zIndex: 10 }}
        >
          {PieSectors}
        </svg>
        <img
          key={`scene-${frame.scene}-thumbnail-${frame.time}`}
          src={frame.src}
          style={{
            position: "absolute",
            left: `${this.state.cx - (frameSizeScale * frame.size[0]) / 2}px`,
            top: `${this.state.cy - (frameSizeScale * frame.size[1]) / 2}px`,
            width: `${frameSizeScale * frame.size[0]}px`,
            height: `${frameSizeScale * frame.size[1]}px`,
            zIndex: 0,
            borderRadius: `50%`,
          }}
        />
      </div>
    );
  }
}

function generatePieSectorPathD(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number
) {
  const six = cx + innerR * Math.cos(startAngle);
  const siy = cy + innerR * Math.sin(startAngle);
  const sox = cx + outerR * Math.cos(startAngle);
  const soy = cy + outerR * Math.sin(startAngle);
  const eox = cx + outerR * Math.cos(endAngle);
  const eoy = cy + outerR * Math.sin(endAngle);
  const eix = cx + innerR * Math.cos(endAngle);
  const eiy = cy + innerR * Math.sin(endAngle);
  if (endAngle - startAngle < Math.PI) {
    return `M ${six} ${siy} L ${sox} ${soy} A ${outerR} ${outerR} 0 0 1 ${eox} ${eoy} L ${eix} ${eiy} A ${innerR} ${innerR} 0 0 0 ${six} ${siy}`;
  } else if (endAngle - startAngle) {
    return `M ${six} ${siy} L ${sox} ${soy} A ${outerR} ${outerR} 0 1 1 ${eox} ${eoy} L ${eix} ${eiy} A ${innerR} ${innerR} 0 1 0 ${six} ${siy}`;
  }
}

function generateTextPosition(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number
) {
  const six = cx + ((innerR + outerR) * Math.cos(startAngle)) / 2;
  const siy = cy + ((innerR + outerR) * Math.sin(startAngle)) / 2;
  return [six, siy];
}
